import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XIcon,
} from '@heroicons/react/outline';
import { Fragment, useEffect, useState } from 'react';

// Custom farm-themed icons/emojis for extra personality
const farmIcons = {
  success: '🌱',
  error: '🚫',
  warning: '⚠️',
  info: '🌾',
};

const typeStyles = {
  success: {
    icon: <CheckCircleIcon className="h-6 w-6 text-green-600 drop-shadow-sm" />,
    bar: 'bg-green-500',
    glow: 'shadow-[0_0_12px_3px_rgba(16,185,129,0.45)]',
    pill: 'bg-green-50/80',
  },
  error: {
    icon: <XCircleIcon className="h-6 w-6 text-red-600 drop-shadow-sm" />,
    bar: 'bg-red-500',
    glow: 'shadow-[0_0_12px_3px_rgba(239,68,68,0.45)]',
    pill: 'bg-red-50/80',
  },
  warning: {
    icon: <ExclamationCircleIcon className="h-6 w-6 text-yellow-600 drop-shadow-sm" />,
    bar: 'bg-yellow-500',
    glow: 'shadow-[0_0_12px_3px_rgba(234,179,8,0.45)]',
    pill: 'bg-yellow-50/80',
  },
  info: {
    icon: <InformationCircleIcon className="h-6 w-6 text-blue-600 drop-shadow-sm" />,
    bar: 'bg-blue-500',
    glow: 'shadow-[0_0_12px_3px_rgba(59,130,246,0.45)]',
    pill: 'bg-blue-50/80',
  },
};

export const Notification = ({ notification: { id, type, title, message }, onDismiss }) => {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => setShow(false), 4000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!show) {
      setTimeout(() => onDismiss(id), 300);
    }
  }, [show, id, onDismiss]);

  const style = typeStyles[type] || typeStyles.info;

  return (
    <div className="w-full flex flex-col items-end sm:items-start pointer-events-auto">
      <Transition
        show={show}
        as={Fragment}
        enter="transform transition duration-300 ease-out"
        enterFrom="opacity-0 translate-y-4 scale-90"
        enterTo="opacity-100 translate-y-0 scale-100"
        leave="transform transition duration-200 ease-in"
        leaveFrom="opacity-100 translate-y-0 scale-100"
        leaveTo="opacity-0 translate-y-2 scale-95"
      >
        <div
          className={`
            relative max-w-sm w-full flex items-center
            rounded-full py-3 px-5
            backdrop-blur-xl border border-white/40
            shadow-lg ${style.glow}
            ${style.pill}
            transition
          `}
        >
          {/* farm emoji for personality */}
          <span className="mr-2 text-xl">{farmIcons[type]}</span>

          {/* message */}
          <div className="ml-3 flex-1">
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            {message && <p className="mt-0.5 text-xs text-gray-700 opacity-80">{message}</p>}
          </div>

          {/* close button */}
          <button
            onClick={() => setShow(false)}
            className="ml-3 text-gray-400 hover:text-gray-700 transition"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>
      </Transition>
    </div>
  );
};
